import { useEffect, useRef, useState } from "react";

// Scroll reveal — adds .is-visible when element enters viewport.
export function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return; // stays visible

    // Only below-the-fold elements get hidden then animated in. Anything already
    // in or above the viewport stays visible (bulletproof against effect timing).
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      el.classList.add("is-visible");
      return;
    }
    el.classList.add("reveal-hidden");
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("is-visible");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return ref;
}

// Track scroll progress past a threshold (fraction of viewport height).
export function useScrolledPast(fraction = 0.24) {
  const [past, setPast] = useState(false);
  useEffect(() => {
    const onScroll = () => setPast(window.scrollY > window.innerHeight * fraction);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [fraction]);
  return past;
}

// Returns true while any element matching selector is intersecting the viewport.
export function useElementInView(selector) {
  const [inView, setInView] = useState(false);
  useEffect(() => {
    let observer;
    const attach = () => {
      const nodes = Array.from(document.querySelectorAll(selector));
      if (!nodes.length) return false;
      observer = new IntersectionObserver(
        (entries) => {
          setInView(entries.some((e) => e.isIntersecting));
        },
        { threshold: 0.05 }
      );
      nodes.forEach((n) => observer.observe(n));
      return true;
    };
    if (!attach()) {
      const t = setInterval(() => {
        if (attach()) clearInterval(t);
      }, 400);
      return () => {
        clearInterval(t);
        if (observer) observer.disconnect();
      };
    }
    return () => observer && observer.disconnect();
  }, [selector]);
  return inView;
}
